import { Colors } from '@cognite/cogs.js';
import {
  Milestone,
  RuleRunStatus,
  RunStatus,
} from 'types/dt-graphql-apollo-v2';
import { sortRunsByDate } from './data-validation';

/** Calculates the percentage of valid data for a given Collection run.
 * The percentage will be calculated at a given index in the run history. */
export const getCollectionRunValidPercentage = (
  collection: Milestone,
  historyIndex: number
) => {
  const { rules } = collection;
  let totalValid = 0;
  let totalFailed = 0;

  rules?.items.forEach((rule) => {
    if (rule && rule.ruleRunStatus) {
      const sortedRuns = sortRunsByDate(
        (rule?.ruleRunStatus.items as RuleRunStatus[]) || []
      );

      totalValid += sortedRuns[historyIndex]?.totalValid || 0;
      totalFailed += sortedRuns[historyIndex]?.totalFailed || 0;
    }
  });

  const totalRuns = totalFailed + totalValid;
  return totalRuns > 0 ? (totalValid / totalRuns) * 100 : 0;
};

/** Gets the history difference for a Collection run.
 *
 * If the Collection has a history, the difference will be calculated using the two most using runs for each rule.
 * Otherwise the difference will be 0, as it's the first unique instance.
 */
export const getCollectionHistoryDifference = (
  collection: Milestone
): number => {
  const collectionHasHistory = collection.rules?.items.some(
    (rule) =>
      rule?.ruleRunStatus?.items.length &&
      rule?.ruleRunStatus?.items.length >= 2
  );
  if (collectionHasHistory) {
    const recentCollectionStats =
      getCollectionRunValidPercentage(collection, 0) || 0;
    const pastCollectionStats =
      getCollectionRunValidPercentage(collection, 1) || 0;

    return recentCollectionStats - pastCollectionStats;
  }
  return 0;
};

/** Returns a LabelVariants color based on the sign of the value received.  */
export const getResultColorVariantFromValue = (
  difference: number
): 'success' | 'danger' | 'normal' => {
  if (difference > 0) return 'success';
  if (difference < 0) return 'danger';
  return 'normal';
};

/** Formats a number when it's a positive value by adding the + prefix. */
export const getFormatedDifference = (difference: number) => {
  if (difference > 0) return `+${difference}`;
  return `${difference}`;
};

/** Returns an IconType value based on the sign of the value received.  */
export const getDiffArrowDirection = (difference: number) => {
  if (difference < 0) return 'ArrowDown';
  if (difference > 0) return 'ArrowUp';
  return 'ArrowRight';
};

/** Returns a CSS color variable for a validity percentage (data consistency). */
export const getValidityColor = (percentage: number): string =>
  percentage >= 90
    ? 'var(--cogs-green)'
    : percentage >= 50
      ? 'var(--cogs-yellow)'
      : 'var(--cogs-red)';

/** Returns a color based on the interval the the percentage fits in. */
export const getResultColorFromPercentage = (percentage: number) => {
  if (percentage >= 90) return 'var(--cogs-green-1)';
  if (percentage < 90 && percentage >= 50) return 'var(--cogs-warning)';
  return 'var(--cogs-danger)';
};

export function isFailedRun(run: RuleRunStatus) {
  const AN_HOUR_AGO = new Date(Date.now() - 60 * 60 * 1000);
  if (
    run.status === RunStatus.InProgress &&
    run.runAt &&
    new Date(run.runAt.toString()) < AN_HOUR_AGO
  ) {
    return {
      ...run,
      status: RunStatus.Error,
    };
  }
  return run;
}
